import { Button, Label, Modal, TextInput, useValidatedState, ValidationHint } from 'acma-spa';
import React, { useEffect, useState } from 'react';
import { getApiClient } from '../api';
import * as yup from 'yup';
import { PublicClientApplication } from '@azure/msal-browser';

export interface AuthorisationModalProps {
  baseUrl: string;
  b2cDomainName: string;
  msalClient: PublicClientApplication;
  open: boolean;
  onClose: (validated: boolean) => any;
}

interface AuthorisationModalState {
  email: string;
  otp: string;
  otpId: string | null;
}

const schema: yup.ObjectSchema<AuthorisationModalState> = yup
  .object({
    email: yup.string().email().required().label('Email'),
    otp: yup
      .string()
      .defined()
      .when('otpId', {
        is: null,
        otherwise: (schema) => schema.required().label('Code'),
      }),
    otpId: yup.string().defined().nullable(),
  })
  .defined();

export const AuthorisationModal: React.FC<AuthorisationModalProps> = (props) => {
  const [model, setModel, errors, validate] = useValidatedState<AuthorisationModalState>(
    {
      email: '',
      otp: '',
      otpId: null,
    },
    schema,
  );

  const [serverError, setServerError] = useState('');
  const [resendOtp, setResendOtp] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (resendOtp == 0) {
      return;
    }
    validate('#newAuthorisation').then((valid) => {
      if (valid) {
        setLoading(true);
        const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
        client
          .authorisationRequest(model.email)
          .then((d) => setModel({ otpId: d }))
          .finally(() => setLoading(false));
      }
    });
  }, [resendOtp]);

  const sendCode = () => {
    setModel({ otpId: null }, ValidationHint.ClearValidation);
    setServerError('');
    setResendOtp(resendOtp + 1);
  };

  const onComplete = async () => {
    if (await validate('#authorisationContainer')) {
      const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
      if (model.otpId) {
        setLoading(true);
        try {
          var d = await client.authorisationConfirm(model.otpId, model.otp);
          if (!d) {
            setServerError('Provided code is invalid');
          } else {
            setModel({ otp: '', otpId: null }, ValidationHint.ClearValidation);
            props.onClose(true);
          }
        } finally {
          setLoading(false);
        }
      }
    }
  };

  return (
    <>
      {props.open ? (
        <Modal
          id="authorisationModal"
          open={true}
          onClose={() => props.onClose(false)}
          title="Link your existing email to our ACMA client records"
          leftButtons={[
            {
              label: 'Confirm',
              onClick: () => onComplete(),
              disabled: loading,
            },
            {
              label: 'Cancel',
              onClick: () => props.onClose(false),
              mode: 'secondary',
            },
          ]}
        >
          <div id="authorisationContainer">
            <Label
              id="email"
              label="Enter the email address you previously used for your ACMA records. We need to send a code to this email address to verify it"
              error={errors.email}
              required
            >
              <TextInput value={model.email} onChange={(v) => setModel({ email: v })} />
            </Label>
            {model.otpId ? (
              <>
                <div className="mt-2" style={{ textAlign: 'center' }}>
                  <Button label="Send new code" onClick={sendCode} disabled={loading} />
                </div>
                <div className="mt-2">
                  <Label id="otp" label="Enter the code sent to this email inbox" error={errors.otp || serverError} required>
                    <TextInput value={model.otp} onChange={(v) => setModel({ otp: v })} />
                  </Label>
                </div>
              </>
            ) : (
              <div className="mt-2" style={{ textAlign: 'center' }}>
                <Button label="Send verification code" onClick={sendCode} disabled={loading} />
              </div>
            )}
          </div>
        </Modal>
      ) : (
        <></>
      )}
    </>
  );
};
